type ApiErrorItem = {
  detail?: string | null;
  code?: string;
  attr?: string | null;
};

type ApiErrorPayload = {
  type?: string;
  message?: string;
  detail?: string;
  errors?: ApiErrorItem[];
};

export type GetApiErrorMessageOptions = {
  firstOnly?: boolean;
};

function collectErrorDetails(data: unknown): string[] {
  if (Array.isArray(data)) {
    return data
      .map(item =>
        item && typeof item === 'object' && typeof (item as ApiErrorItem).detail === 'string'
          ? (item as ApiErrorItem).detail!.trim()
          : ''
      )
      .filter(Boolean);
  }

  if (!data || typeof data !== 'object') {
    return [];
  }

  const payload = data as ApiErrorPayload;

  if (Array.isArray(payload.errors) && payload.errors.length > 0) {
    return payload.errors
      .map(e => (typeof e?.detail === 'string' ? e.detail.trim() : ''))
      .filter(Boolean);
  }

  if (typeof payload.detail === 'string' && payload.detail.trim()) {
    return [payload.detail.trim()];
  }

  return [];
}

function formatErrorParts(parts: string[], options?: GetApiErrorMessageOptions): string {
  if (options?.firstOnly || parts.length === 1) {
    return parts[0];
  }
  return parts.map((msg, i) => `${i + 1}. ${msg}`).join(' ');
}

export function getApiErrorMessage(
  error: unknown,
  fallback: string,
  options?: GetApiErrorMessageOptions
): string {
  const err = error as { data?: unknown; message?: string } | undefined;
  const fromData = collectErrorDetails(err?.data);

  if (fromData.length > 0) {
    return formatErrorParts(fromData, options);
  }

  const payload = err?.data;
  if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
    const obj = payload as ApiErrorPayload;
    if (typeof obj.message === 'string' && obj.message.trim()) {
      return obj.message.trim();
    }
  }

  if (typeof err?.message === 'string' && err.message.trim()) {
    return err.message.trim();
  }

  return fallback;
}
